import React, {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import cx from 'classnames';
import {
    defaultDropDownV2Context,
    DropdownV2,
    DropDownV2Context,
    Input,
    ToolbarIcons,
    useDropdown
} from '@priority-software/priority-style-react';
import {useEventCallback} from 'usehooks-ts';
import {transformColor} from './utils';
import {HEIGHT, WIDTH} from './constants';
import {MoveWrapper, Position} from './Palette';
import {IconWithPopover} from '../IconWithPopover';
import {createDataTestId} from '../../../../../utils/dataTestId';
import {keys} from '../../../../../constants/KeyCodes';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';

interface ColorPickerProps {
    disabled?: boolean;
    isWithoutPlate?: boolean;
    buttonAriaLabel?: string;
    buttonClassName: string;
    buttonIconClassName?: string;
    buttonLabel?: string;
    color: string;
    children?: ReactNode;
    onChange?: (color: string) => void;
    title?: string;
    colorsPalette: string[];
    content?: string;
    usePortal?: boolean;
    portalSelector?: string;
}

export default function ColorPicker(props: ColorPickerProps) {
    const {
        color,
        children,
        onChange,
        colorsPalette,
        isWithoutPlate = false,
        content = 'Formatting text color',
        usePortal,
        portalSelector,
        disabled
    } = props;
    const [editor] = useLexicalComposerContext();
    const [selfColor, setSelfColor] = useState(transformColor('hex', color));
    const [inputColor, setInputColor] = useState(color);
    const innerDivRef = useRef(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const {isOpen, toggleOpen, closeDropdown, openDropdown} = useDropdown({
        onClose: () => setIsDropdownOpen(false),
        onOpen: () => setIsDropdownOpen(true)
    });

    const saturationPosition = useMemo(
        () => ({
            x: (selfColor.hsv.s / 100) * WIDTH,
            y: ((100 - selfColor.hsv.v) / 100) * HEIGHT
        }),
        [selfColor.hsv.s, selfColor.hsv.v]
    );

    const huePosition = useMemo(() => ({x: (selfColor.hsv.h / 360) * WIDTH}), [selfColor.hsv]);

    const onSetHex = (hex: string) => {
        setInputColor(hex);
        if (/^#[0-9A-Fa-f]{6}$/i.test(hex)) {
            const newColor = transformColor('hex', hex);
            setSelfColor(newColor);
        }
    };

    const onMoveSaturation = ({x, y}: Position) => {
        const newHsv = {
            ...selfColor.hsv,
            s: (x / WIDTH) * 100,
            v: 100 - (y / HEIGHT) * 100
        };
        const newColor = transformColor('hsv', newHsv);
        setSelfColor(newColor);
        setInputColor(newColor.hex);
    };

    const onMoveHue = ({x}: Position) => {
        const newHsv = {...selfColor.hsv, h: (x / WIDTH) * 360};
        const newColor = transformColor('hsv', newHsv);

        setSelfColor(newColor);
        setInputColor(newColor.hex);
    };

    useEffect(() => {
        if (innerDivRef.current !== null && onChange) {
            onChange(selfColor.hex);
            setInputColor(selfColor.hex);

            // Restore focus to input after state update
            requestAnimationFrame(() => {
                inputRef.current?.focus();
            });
        }
    }, [selfColor, onChange]);

    useEffect(() => {
        if (color === undefined) return;
        const newColor = transformColor('hex', color);
        setSelfColor(newColor);
        setInputColor(newColor.hex);
    }, [color]);

    const handleToggle = useEventCallback((e: React.MouseEvent<HTMLButtonElement>) => {
        if (isOpen) {
            closeDropdown();
        } else {
            // Before opening the dropdown menu should lose the focus from button component
            // in order to prevent click event on trigger element while user press enter
            e.currentTarget.blur();
            openDropdown();
        }
    });

    return (
        <DropDownV2Context.Provider
            value={{
                ...defaultDropDownV2Context,
                isOpen: isDropdownOpen,
                toggleOpen,
                closeDropdown,
                openDropdown
            }}
        >
            <DropdownV2
                {...createDataTestId(`color-picker-dropdown-container`, true)}
                onClose={closeDropdown}
                isOpen={!disabled && isDropdownOpen}
                portalSelector={portalSelector}
                usePortal={usePortal}
                trigger={
                    <IconWithPopover
                        type="icon"
                        content={content}
                        onClick={handleToggle}
                        componentClasses={cx('priority-editor__toolbar-item')}
                        icon={ToolbarIcons.toolbarFormatColor}
                        disabled={disabled}
                    />
                }
            >
                <div
                    className="color-picker-wrapper"
                    style={{width: WIDTH}}
                    ref={innerDivRef}
                    {...createDataTestId(`color-picker-dropdown-list-container`, false)}
                >
                    <Input
                        {...createDataTestId(`color-picker-dropdown-input`, true)}
                        inputRef={inputRef}
                        size="small"
                        label="Hex"
                        disabled={isWithoutPlate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            onSetHex(e.target.value);
                        }}
                        value={inputColor}
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                            if (e.key === keys.ENTER) {
                                e.stopPropagation();
                                e.preventDefault();

                                closeDropdown();
                                editor.focus();
                            }
                        }}
                    />

                    <div
                        className="color-picker__colors"
                        {...createDataTestId(`color-picker-plaette-container`, false)}
                    >
                        {colorsPalette.map(basicColor => (
                            <button
                                {...createDataTestId(
                                    `color-picker-plaette-button-${
                                        basicColor === selfColor.hex ? 'active' : 'inactive'
                                    }`,
                                    false
                                )}
                                className={basicColor === selfColor.hex ? ' active' : ''}
                                key={basicColor}
                                style={{backgroundColor: basicColor}}
                                onClick={() => {
                                    setInputColor(basicColor);
                                    setSelfColor(transformColor('hex', basicColor));
                                }}
                            />
                        ))}
                    </div>
                    {isWithoutPlate || (
                        <>
                            <MoveWrapper
                                className="color-picker-saturation"
                                style={{backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`}}
                                onChange={onMoveSaturation}
                            >
                                <div
                                    className="color-picker-saturation_cursor"
                                    style={{
                                        backgroundColor: selfColor.hex,
                                        left: saturationPosition.x,
                                        top: saturationPosition.y
                                    }}
                                />
                            </MoveWrapper>

                            <MoveWrapper className="color-picker-hue" onChange={onMoveHue}>
                                <div
                                    className="color-picker-hue_cursor"
                                    style={{
                                        backgroundColor: `hsl(${selfColor.hsv.h}, 100%, 50%)`,
                                        left: huePosition.x
                                    }}
                                />
                            </MoveWrapper>
                            <div
                                className="color-picker-color"
                                style={{backgroundColor: selfColor.hex}}
                            />
                        </>
                    )}
                </div>
                {children}
            </DropdownV2>
        </DropDownV2Context.Provider>
    );
}
